import { getDeviceId } from '../utils/deviceUtils';

const API_BASE_URL = import.meta.env.VITE_NEW_WORDS_API_URL || 'http://localhost:8080';

export interface ChallengeConfig {
  sessionId: string;
  // Wordle-only fields, present at the top level for backward compatibility
  // (the backend flattens the engine's payload onto the response). Absent
  // for other challenge types - read `payload` instead for those.
  wordLength: number;
  maxGuesses: number;
  guessCount: number;
  guesses: any[];
  status: 'active' | 'won' | 'lost';
  targetWord?: string;
  setterNote?: any;
  definition?: string;
  /**
   * Elapsed solve time in seconds, only present when resuming an
   * already-completed session (status !== 'active') - lets a client that
   * never observed the live completion (new device, cleared local storage)
   * still record how long that session took.
   */
  totalTimeSeconds?: number;
  /** Engine-specific fields (e.g. Anagram's `scrambled`/`attemptCount`). */
  payload?: Record<string, unknown>;
}

export interface GuessResult {
  // Wordle-only fields, present at the top level for backward compatibility.
  pattern: ('correct' | 'present' | 'absent')[];
  status: 'active' | 'won' | 'lost';
  attemptsRemaining: number;
  targetWord?: string;
  error?: string;
  setterNote?: any;
  definition?: string;
  /** Engine-specific fields (e.g. Anagram's `correct`/`attemptCount`). */
  payload?: Record<string, unknown>;
}

class ChallengeApi {
  /**
   * Start or resume a challenge session
   */
  async startChallenge(params: {
    challengeType?: string;
    mode?: string;
    language?: string;
    length?: number;
    userId?: string;
    guestName?: string;
  }): Promise<ChallengeConfig> {
    const { challengeType, mode, language, length, userId, guestName } = params;
    const deviceId = getDeviceId();

    const response = await fetch(`${API_BASE_URL}/api/challenge/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Id': deviceId,
      },
      body: JSON.stringify({
        challengeType,
        mode,
        language,
        length,
        userId,
        guestName,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to start challenge');
    }

    return response.json();
  }

  /**
   * Set/rename the guest display name shown on the leaderboard for this device
   */
  async updateGuestName(guestName: string): Promise<{ guestName: string }> {
    const deviceId = getDeviceId();

    const response = await fetch(`${API_BASE_URL}/api/challenge/guest-name`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Id': deviceId,
      },
      body: JSON.stringify({ guestName }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update guest name');
    }

    return response.json();
  }

  /**
   * Submit an attempt (a Wordle guess, an Anagram rearrangement, etc. -
   * whichever the session's engine expects). Sends `attempt` as the
   * canonical field name; the backend also still accepts `guess` for
   * already-deployed clients during a rolling release.
   */
  async submitGuess(sessionId: string, attempt: string): Promise<GuessResult> {
    const response = await fetch(`${API_BASE_URL}/api/challenge/guess`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sessionId,
        attempt,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to submit guess');
    }

    return response.json();
  }

  /**
   * Get the global leaderboard
   */
  async getLeaderboard(params: {
    date?: string;
    mode?: string;
    language?: string;
    length?: number;
    challengeType?: string;
  }): Promise<any[]> {
    const query = new URLSearchParams();
    if (params.date) query.append('date', params.date);
    if (params.mode) query.append('mode', params.mode);
    if (params.language) query.append('language', params.language);
    if (params.length) query.append('length', params.length.toString());
    if (params.challengeType) query.append('challengeType', params.challengeType);

    const response = await fetch(`${API_BASE_URL}/api/leaderboard?${query.toString()}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch leaderboard');
    }

    return response.json();
  }
}

export const challengeApi = new ChallengeApi();
